<template>
  <div class="space-y-5">

    <!-- Info Modal -->
    <teleport to="body">
      <div v-if="infoModal.show" class="fixed inset-0 z-50 flex items-center justify-center">
        <div class="absolute inset-0 bg-black/70" @click="infoModal.show = false"></div>
        <div class="relative bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
          <div class="flex items-start justify-between mb-3">
            <h4 class="text-sm font-semibold text-white pr-4">{{ infoModal.title }}</h4>
            <button @click="infoModal.show = false" class="text-gray-500 hover:text-white transition flex-shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          <p class="text-sm text-gray-400 leading-relaxed">{{ infoModal.description }}</p>
        </div>
      </div>
    </teleport>

    <!-- Tab Navigation -->
    <div class="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
      <!-- Execution Profile header -->
      <div class="px-6 py-3 border-b border-gray-800 bg-gray-800/20 flex items-center justify-between gap-4 flex-wrap">
        <div class="flex items-center gap-4 text-xs text-gray-500">
          <span>👤 <span class="text-white font-medium">{{ authStore.user?.username || '-' }}</span></span>
          <span class="text-gray-700">|</span>
          <span>Bot ativo: <span class="font-medium" :class="runtimeProfile.currentProfile?.is_ready ? 'text-green-400' : 'text-yellow-400'">{{ runtimeProfile.currentProfile?.is_ready ? 'Pronto' : 'Faltam credenciais' }}</span></span>
          <span class="text-gray-700">|</span>
          <span>Status: <span class="font-medium" :class="botStatus.isRunning ? 'text-green-400' : 'text-red-400'">{{ botStatus.isRunning ? 'Rodando' : 'Parado' }}</span></span>
        </div>
        <div class="flex gap-2">
          <button @click="startBot" :disabled="botStatus.isRunning" class="action-green">Iniciar</button>
          <button @click="stopBot" :disabled="!botStatus.isRunning" class="action-red">Parar</button>
          <button @click="restartBot" class="action-yellow">Reiniciar</button>
        </div>
      </div>

      <!-- Tabs -->
      <div class="flex border-b border-gray-800 overflow-x-auto">
        <button v-for="tab in tabs" :key="tab.id"
          v-show="!tab.adminOnly || authStore.user?.role === 'admin'"
          @click="activeTab = tab.id"
          class="flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-all whitespace-nowrap"
          :class="activeTab === tab.id
            ? 'border-blue-500 text-white bg-blue-500/5'
            : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-600'"
        >
          <span>{{ tab.icon }}</span>{{ tab.label }}
        </button>
      </div>
    </div>

    <!-- ── TAB: CREDENCIAIS ─────────────────────────────── -->
    <div v-show="activeTab === 'credentials'">
      <form @submit.prevent="saveCredentials" class="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
        <div class="px-6 py-4 border-b border-gray-800 bg-gray-800/20">
          <h3 class="text-sm font-semibold text-white">🔑 Credenciais de Acesso</h3>
          <p class="text-xs text-gray-500 mt-0.5">Polymarket wallet e Gemini API do usuário autenticado.</p>
        </div>
        <div class="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label class="field-label">Private Key Polymarket <button type="button" @click="showInfo('Private Key Polymarket', 'Chave privada da sua carteira Polymarket. Necessária para assinar transações e executar apostas no mercado.')" class="info-btn">ⓘ</button></label>
            <input v-model="credentials.polymarket_private_key" type="password" class="input" placeholder="0x..." />
          </div>
          <div>
            <label class="field-label">Carteira / Funder Address <button type="button" @click="showInfo('Carteira / Funder Address', 'Endereço público da carteira que financia as apostas. Deve corresponder à private key informada.')" class="info-btn">ⓘ</button></label>
            <input v-model="credentials.polymarket_funder_address" type="text" class="input" placeholder="0x..." />
          </div>
          <div>
            <label class="field-label">Gemini API Key <button type="button" @click="showInfo('Gemini API Key', 'Chave de API do Google Gemini utilizada para análise de mercados com inteligência artificial.')" class="info-btn">ⓘ</button></label>
            <input v-model="credentials.gemini_api_key" type="password" class="input" />
          </div>
          <div>
            <label class="field-label">CoinGecko API Key <button type="button" @click="showInfo('CoinGecko API Key', 'Chave de API do CoinGecko para dados de preço. Opcional — sem ela o bot usa dados públicos com limite de taxa.')" class="info-btn">ⓘ</button></label>
            <input v-model="credentials.coingecko_api_key" type="password" class="input" placeholder="Opcional" />
          </div>
          <div>
            <label class="field-label">Saldo Inicial (USDC) <button type="button" @click="showInfo('Saldo Inicial', 'Saldo inicial da sua banca em USDC. Usado como base para cálculo de ROI e pools de risco.')" class="info-btn">ⓘ</button></label>
            <input v-model.number="credentials.initial_balance" type="number" step="0.01" class="input" />
          </div>
        </div>
        <div class="px-6 pb-6">
          <button type="submit" class="w-full bg-gradient-to-r from-blue-600 to-cyan-500 px-6 py-3 rounded-xl text-sm font-semibold transition-all">
            Salvar Credenciais
          </button>
        </div>
      </form>
    </div>

    <!-- ── TAB: NEGOCIAÇÃO ─────────────────────────────── -->
    <div v-show="activeTab === 'trading'">
      <form @submit.prevent="saveSettings" class="space-y-4">
        <!-- Banca e Apostas -->
        <div class="section-card">
          <div class="section-header">💰 Banca e Apostas</div>
          <div class="p-5 grid grid-cols-1 md:grid-cols-3 gap-5">
            <div>
              <label class="field-label">Saldo Inicial (USDC) <button type="button" @click="showInfo('Saldo Inicial', 'Saldo inicial da sua banca em USDC. Usado como base para cálculo de ROI.')" class="info-btn">ⓘ</button></label>
              <input v-model.number="settings.initialBalance" type="number" step="0.01" class="input" />
            </div>
            <div>
              <label class="field-label">Aposta Padrão (USDC) <button type="button" @click="showInfo('Tamanho Padrão de Aposta', 'Valor em USDC de cada aposta quando a pool não define um mínimo específico.')" class="info-btn">ⓘ</button></label>
              <input v-model.number="settings.defaultBetSize" type="number" step="0.01" class="input" />
            </div>
            <div>
              <label class="field-label">Multiplicador de Banca <button type="button" @click="showInfo('Multiplicador de Banca', 'Fator de crescimento. Ex: 1.1 = o bot precisa de 10% de lucro para aumentar o limite de risco.')" class="info-btn">ⓘ</button></label>
              <input v-model.number="settings.bankGrowthMultiplier" type="number" step="0.01" min="1" class="input" />
            </div>
          </div>
        </div>

        <!-- Pools de Risco -->
        <div class="section-card">
          <div class="section-header flex items-center gap-2">🎲 Pools de Risco <button type="button" @click="showInfo('Pools de Risco', 'Porcentagem do saldo reservada para cada nível.\nCONSERVADOR: conf ≥ (threshold + 10%)\nMODERADO: conf ≥ threshold\nEXTREMO: conf ≥ conf mínima extremo (configurável)\nTrades abaixo da conf mínima extremo são descartados.')" class="info-btn text-base">ⓘ</button></div>
          <div class="p-5 grid grid-cols-3 gap-4">
            <div class="bg-teal-900/20 border border-teal-700/50 rounded-xl p-4">
              <div class="text-teal-400 font-bold text-xs mb-3 uppercase">Conservador <span class="font-normal text-teal-600">conf ≥ thresh+10%</span></div>
              <label class="text-xs text-gray-400 block mb-1">% do Saldo</label>
              <input type="number" v-model.number="settings.conservativePoolPercent" min="0" max="100" step="5" class="input mb-3" />
              <label class="text-xs text-gray-400 block mb-1">Aposta Mínima ($)</label>
              <input type="number" v-model.number="settings.conservativeMinBet" min="0.5" step="0.5" class="input" />
            </div>
            <div class="bg-blue-900/20 border border-blue-700/50 rounded-xl p-4">
              <div class="text-blue-400 font-bold text-xs mb-3 uppercase">Moderado <span class="font-normal text-blue-600">conf ≥ threshold</span></div>
              <label class="text-xs text-gray-400 block mb-1">% do Saldo</label>
              <input type="number" v-model.number="settings.moderatePoolPercent" min="0" max="100" step="5" class="input mb-3" />
              <label class="text-xs text-gray-400 block mb-1">Aposta Mínima ($)</label>
              <input type="number" v-model.number="settings.moderateMinBet" min="0.5" step="0.5" class="input" />
            </div>
            <div class="bg-violet-900/20 border border-violet-700/50 rounded-xl p-4">
              <div class="text-violet-400 font-bold text-xs mb-3 uppercase">Extremo <span class="font-normal text-violet-600">conf ≥ mín extremo</span></div>
              <label class="text-xs text-gray-400 block mb-1">% do Saldo</label>
              <input type="number" v-model.number="settings.extremePoolPercent" min="0" max="100" step="5" class="input mb-3" />
              <label class="text-xs text-gray-400 block mb-1">Aposta Mínima ($)</label>
              <input type="number" v-model.number="settings.extremeMinBet" min="0.5" step="0.5" class="input mb-3" />
              <label class="text-xs text-gray-400 block mb-1">Conf. Mínima Extremo (%) <button type="button" @click="showInfo('Conf. Mínima Extremo', 'Confiança mínima para entrar em trades EXTREMO. Era hardcoded em 35% — causando apostas com conf=40% (valor esperado negativo). Recomendado: 50–55%.')" class="info-btn">ⓘ</button></label>
              <input type="number" v-model.number="settings.extremeMinConfidence" min="30" max="65" step="1" class="input" />
            </div>
          </div>
        </div>

        <!-- IA -->
        <div class="section-card">
          <div class="section-header">🤖 Análise e IA</div>
          <div class="p-5 grid grid-cols-1 md:grid-cols-3 gap-5">
            <div>
              <label class="field-label">Confiança Mínima (%) <button type="button" @click="showInfo('Confiança Mínima', 'Threshold base de confiança. MODERADO: conf ≥ este valor. CONSERVADOR: conf ≥ este valor + 10%. EXTREMO usa um threshold próprio configurado em Pools de Risco.')" class="info-btn">ⓘ</button></label>
              <input v-model.number="settings.minConfidenceThreshold" type="number" min="0" max="100" class="input" />
            </div>
            <div>
              <label class="field-label">Intervalo de Análise (min) <button type="button" @click="showInfo('Intervalo de Análise', 'Tempo em minutos entre cada ciclo de análise de mercados.')" class="info-btn">ⓘ</button></label>
              <input v-model.number="settings.analysisIntervalMinutes" type="number" min="1" class="input" />
            </div>
            <div>
              <label class="field-label">Max Mercados por Ciclo <button type="button" @click="showInfo('Max Mercados por Ciclo', 'Quantos mercados são analisados por ciclo. Mais = mais ordens mas ciclo mais lento.')" class="info-btn">ⓘ</button></label>
              <input v-model.number="settings.maxAnalysisMarkets" type="number" min="4" class="input" />
            </div>
            <div>
              <label class="field-label">Timeout de Ordem (min) <button type="button" @click="showInfo('Timeout de Ordem', 'Minutos antes de cancelar uma ordem não preenchida.')" class="info-btn">ⓘ</button></label>
              <input v-model.number="settings.orderTimeoutMinutes" type="number" min="1" class="input" />
            </div>
          </div>
        </div>

        <div class="flex gap-4">
          <button type="submit" class="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-3 rounded-xl text-sm font-semibold">Salvar</button>
          <button type="button" @click="resetDefaults" class="flex-1 bg-gray-800 hover:bg-gray-700 border border-gray-700 px-6 py-3 rounded-xl text-sm font-semibold">Restaurar Padrão</button>
        </div>
      </form>
    </div>

    <!-- ── TAB: MERCADOS ────────────────────────────────── -->
    <div v-show="activeTab === 'markets'">
      <form @submit.prevent="saveSettings" class="space-y-4">
        <div class="section-card">
          <div class="section-header">🎯 Filtros de Mercado</div>
          <div class="p-5 grid grid-cols-1 md:grid-cols-3 gap-5">
            <div>
              <label class="field-label">Expiração Máxima (h) <button type="button" @click="showInfo('Expiração Máxima', 'Só entrar em mercados que expiram em até X horas.')" class="info-btn">ⓘ</button></label>
              <input v-model.number="settings.maxHoursToExpiry" type="number" step="0.5" min="0.5" class="input" />
            </div>
            <div>
              <label class="field-label">Janela de Fechamento (min) <button type="button" @click="showInfo('Janela de Fechamento', 'Só entrar em mercados nos últimos X minutos antes do fechamento.')" class="info-btn">ⓘ</button></label>
              <input v-model.number="settings.closeWindowMinutes" type="number" min="1" max="10" step="0.5" class="input" />
            </div>
            <div>
              <label class="field-label">Max Posições Simultâneas <button type="button" @click="showInfo('Max Posições Simultâneas', 'Número máximo de apostas abertas ao mesmo tempo.')" class="info-btn">ⓘ</button></label>
              <input v-model.number="settings.maxConcurrentPositions" type="number" min="1" class="input" />
            </div>
            <div>
              <label class="field-label">Exposição Máx Aberta (%) <button type="button" @click="showInfo('Exposição Max Aberta %', 'Percentual máximo do saldo que pode estar em apostas abertas simultaneamente.')" class="info-btn">ⓘ</button></label>
              <input v-model.number="settings.maxOpenExposurePercent" type="number" step="0.1" min="1" max="100" class="input" />
            </div>
          </div>
          <!-- Daily Stop Loss -->
          <div class="p-5 pt-0 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div class="config-item">
              <label class="field-label">Saldo Mínimo (USD) <button type="button" @click="showInfo('Saldo Mínimo (USD)', 'Piso de saldo: se o saldo cair até este valor o bot para de operar automaticamente. Só volta quando o saldo for recarregado ou o valor for alterado. 0 = desativado.')" class="info-btn">ⓘ</button></label>
              <div class="text-[10px] text-gray-500 mb-1">0 = desativado. Bot para quando saldo atingir este valor.</div>
              <input type="number" v-model.number="settings.dailyStopLossUsd" min="0" step="1" class="input" />
            </div>
          </div>
        </div>

        <div class="section-card">
          <div class="section-header">📐 Faixas de Preço</div>
          <div class="p-5 grid grid-cols-2 md:grid-cols-4 gap-5">
            <div>
              <label class="field-label">Edge Min <button type="button" @click="showInfo('Edge Min Price', 'Preço mínimo para mercados acima/abaixo (edge markets).')" class="info-btn">ⓘ</button></label>
              <input v-model.number="settings.edgeMinPrice" type="number" step="0.01" min="0" max="1" class="input" />
            </div>
            <div>
              <label class="field-label">Edge Max <button type="button" @click="showInfo('Edge Max Price', 'Preço máximo para mercados acima/abaixo.')" class="info-btn">ⓘ</button></label>
              <input v-model.number="settings.edgeMaxPrice" type="number" step="0.01" min="0" max="1" class="input" />
            </div>
            <div>
              <label class="field-label">Coinflip Min <button type="button" @click="showInfo('Coinflip Min Price', 'Preço mínimo para mercados up or down (coinflip).')" class="info-btn">ⓘ</button></label>
              <input v-model.number="settings.coinflipMinPrice" type="number" step="0.01" min="0" max="1" class="input" />
            </div>
            <div>
              <label class="field-label">Coinflip Max <button type="button" @click="showInfo('Coinflip Max Price', 'Preço máximo para mercados up or down.')" class="info-btn">ⓘ</button></label>
              <input v-model.number="settings.coinflipMaxPrice" type="number" step="0.01" min="0" max="1" class="input" />
            </div>
          </div>
        </div>

        <div class="flex gap-4">
          <button type="submit" class="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-3 rounded-xl text-sm font-semibold">Salvar</button>
          <button type="button" @click="resetDefaults" class="flex-1 bg-gray-800 hover:bg-gray-700 border border-gray-700 px-6 py-3 rounded-xl text-sm font-semibold">Restaurar Padrão</button>
        </div>
      </form>
    </div>

    <!-- ── TAB: PROTEÇÃO ────────────────────────────────── -->
    <div v-show="activeTab === 'protection'">
      <form @submit.prevent="saveSettings" class="space-y-4">
        <!-- Stop Loss -->
        <div class="section-card">
          <div class="section-header">🛡️ Stop Loss</div>
          <div class="p-5 grid grid-cols-1 md:grid-cols-3 gap-5">
            <div>
              <label class="field-label">Stop Loss (%) <button type="button" @click="showInfo('Stop Loss %', 'Percentual de queda do preço que aciona o stop loss. Ex: 10 = vende se o preço cair 10%.')" class="info-btn">ⓘ</button></label>
              <input v-model.number="settings.stopLossPercent" type="number" step="0.1" min="0" max="100" class="input" />
            </div>
            <div>
              <label class="field-label">Preço Crítico Stop <button type="button" @click="showInfo('Preço Crítico Stop', 'Se o preço do token cair abaixo desse valor (ex: 0.18), vende imediatamente.')" class="info-btn">ⓘ</button></label>
              <input v-model.number="settings.criticalStopPrice" type="number" step="0.01" min="0" max="1" class="input" />
            </div>
            <div>
              <label class="field-label">Reta Final Stop (s) <button type="button" @click="showInfo('Reta Final Stop', 'Segundos antes do mercado fechar onde o stop é ativado se a posição estiver perdendo.')" class="info-btn">ⓘ</button></label>
              <input v-model.number="settings.lateStageStopSeconds" type="number" min="60" class="input" />
            </div>
            <div>
              <label class="field-label">Risco Máximo por Trade (%) <button type="button" @click="showInfo('Risco Máximo por Trade', 'Porcentagem máxima da banca que pode ser arriscada em um único trade.')" class="info-btn">ⓘ</button></label>
              <input v-model.number="settings.maxRiskPerTrade" type="number" min="0" max="100" class="input" />
            </div>
            <div>
              <label class="field-label">Drawdown Máx do Pico (%) <button type="button" @click="showInfo('Drawdown Max Pico', 'Se o saldo cair X% do pico histórico, o bot para de operar.')" class="info-btn">ⓘ</button></label>
              <input v-model.number="settings.maxPeakDrawdownPercent" type="number" step="0.1" min="1" max="100" class="input" />
            </div>
            <div>
              <label class="field-label">Threshold de Claim (%) <button type="button" @click="showInfo('Threshold de Claim', 'Percentual de lucro mínimo para fazer claim automático.')" class="info-btn">ⓘ</button></label>
              <input v-model.number="settings.profitClaimThreshold" type="number" min="0" max="100" class="input" />
            </div>
          </div>
          <div class="px-5 pb-5">
            <button type="button" @click="saveSettings"
              class="w-full bg-gradient-to-r from-red-700 to-rose-600 px-6 py-3 rounded-xl text-sm font-semibold transition-all hover:opacity-90">
              💾 Salvar Stop Loss
            </button>
          </div>
        </div>

        <!-- Hero Checkpoint -->
        <div class="section-card">
          <div class="section-header">🦸 Hero Checkpoint</div>
          <div class="p-5 space-y-4">
            <div>
              <label class="field-label">Ativar Hero Checkpoint <button type="button" @click="showInfo('Hero Check Point', 'Protege lucros após atingir meta. Quando ativo, vende se o lucro retrair X% do pico.')" class="info-btn">ⓘ</button></label>
              <button type="button" @click="settings.heroCheckpointEnabled = !settings.heroCheckpointEnabled" class="toggle-card w-full mt-1.5">
                <span class="text-sm">Protege lucro após pico</span>
                <span class="text-sm font-bold" :class="settings.heroCheckpointEnabled ? 'text-emerald-400' : 'text-gray-500'">
                  {{ settings.heroCheckpointEnabled ? '✓ Ativo' : 'Desligado' }}
                </span>
              </button>
            </div>
            <div class="grid grid-cols-2 gap-5" :class="{ 'opacity-40 pointer-events-none': !settings.heroCheckpointEnabled }">
              <div>
                <label class="field-label">Ativa no Lucro (%) <button type="button" @click="showInfo('Hero Ativa no Lucro %', 'Percentual de lucro necessário para ativar o Hero Checkpoint.')" class="info-btn">ⓘ</button></label>
                <input v-model.number="settings.heroActivationProfitPercent" type="number" step="1" min="0" max="1000" class="input" />
              </div>
              <div>
                <label class="field-label">Retração (%) <button type="button" @click="showInfo('Hero Retração %', 'Se o lucro cair X% do pico após ativar o Hero, vende tudo.')" class="info-btn">ⓘ</button></label>
                <input v-model.number="settings.heroRetracePercent" type="number" step="1" min="1" max="1000" class="input" />
              </div>
            </div>
          </div>
          <div class="px-5 pb-5">
            <button type="button" @click="saveSettings"
              class="w-full bg-gradient-to-r from-emerald-700 to-green-600 px-6 py-3 rounded-xl text-sm font-semibold transition-all hover:opacity-90">
              💾 Salvar Hero Checkpoint
            </button>
          </div>
        </div>

        <!-- Cooldown de Re-entrada -->
        <div class="section-card">
          <div class="section-header">🔒 Cooldown de Re-entrada</div>
          <div class="p-5 space-y-5">
            <div>
              <label class="field-label">Ativar Cooldown Automático <button type="button" @click="showInfo('Cooldown de Re-entrada', 'Quando ativado, o bot bloqueia temporariamente uma crypto após acumular perdas consecutivas, evitando entrar no mesmo ativo em queda. O bloqueio só é acionado quando AMBOS os limites (número de perdas E valor em USDC) forem atingidos simultaneamente.')" class="info-btn">ⓘ</button></label>
              <button type="button" @click="notif.cooldownEnabled = !notif.cooldownEnabled" class="toggle-card w-full mt-1.5">
                <span class="text-sm">Bloquear re-entrada após perdas consecutivas</span>
                <span class="text-sm font-bold" :class="notif.cooldownEnabled ? 'text-orange-400' : 'text-gray-500'">
                  {{ notif.cooldownEnabled ? '✓ Ativo' : 'Desligado' }}
                </span>
              </button>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-5" :class="{ 'opacity-40 pointer-events-none': !notif.cooldownEnabled }">
              <div>
                <label class="field-label">Perdas para bloquear <button type="button" @click="showInfo('Perdas para bloquear', 'Número mínimo de apostas perdidas consecutivamente na mesma crypto para acionar o cooldown. Ex: 3 = só bloqueia após a 3ª perda seguida no BTC.')" class="info-btn">ⓘ</button></label>
                <input v-model.number="notif.cooldownMinLosses" type="number" min="1" max="20" class="input" />
              </div>
              <div>
                <label class="field-label">Perda mínima (USDC) <button type="button" @click="showInfo('Perda mínima em USDC', 'Valor total acumulado de perdas na mesma crypto necessário para acionar o bloqueio. Ex: 5 = só bloqueia se as perdas somarem ao menos $5 USDC. Serve como filtro — uma perda de $0.10 não trava a crypto.')" class="info-btn">ⓘ</button></label>
                <input v-model.number="notif.cooldownMinLossUsdc" type="number" min="0.5" step="0.5" class="input" />
              </div>
              <div>
                <label class="field-label">Duração do bloqueio (min) <button type="button" @click="showInfo('Duração do bloqueio', 'Quanto tempo a crypto fica bloqueada após atingir os limites. Ex: 240 = 4 horas sem entrar em BTC após o bloqueio ser acionado. Você pode desbloquear manualmente no Dashboard a qualquer momento.')" class="info-btn">ⓘ</button></label>
                <input v-model.number="notif.cooldownMinutes" type="number" min="30" max="1440" step="30" class="input" />
              </div>
            </div>
            <p class="text-xs text-gray-600">O bloqueio é acionado somente quando <span class="text-gray-400 font-semibold">ambos</span> os limites forem atingidos. Para desbloquear manualmente antes do tempo, acesse o painel no Dashboard. O acumulador de perdas é zerado quando o bloqueio expira ou é removido.</p>

            <div class="flex gap-4 pt-1">
              <button type="button" @click="saveNotifications"
                class="flex-1 bg-gradient-to-r from-orange-600 to-amber-500 px-6 py-3 rounded-xl text-sm font-semibold transition-all hover:opacity-90">
                💾 Salvar Configurações de Cooldown
              </button>
            </div>
            <p v-if="notif.saveMsg" class="text-center text-xs" :class="notif.saveOk ? 'text-green-400' : 'text-red-400'">{{ notif.saveMsg }}</p>
          </div>
        </div>

      </form>
    </div>

    <!-- ── TAB: BOT & LOGS ──────────────────────────────── -->
    <div v-show="activeTab === 'bot'">
      <!-- Poll Intervals -->
      <form @submit.prevent="saveSettings" class="space-y-4 mb-4">
        <div class="section-card">
          <div class="section-header">⏱️ Intervalos de Polling</div>
          <div class="p-5 grid grid-cols-2 md:grid-cols-4 gap-5">
            <div>
              <label class="field-label">Poll Análise (s) <button type="button" @click="showInfo('Poll de Análise', 'Frequência de verificação do ciclo de análise de mercados.')" class="info-btn">ⓘ</button></label>
              <input v-model.number="settings.analysisPollSeconds" type="number" min="5" class="input" />
            </div>
            <div>
              <label class="field-label">Poll Claims (s) <button type="button" @click="showInfo('Poll de Claims', 'Frequência de verificação de resgates automáticos.')" class="info-btn">ⓘ</button></label>
              <input v-model.number="settings.claimPollSeconds" type="number" min="5" class="input" />
            </div>
            <div>
              <label class="field-label">Poll Ordens (s) <button type="button" @click="showInfo('Poll de Ordens', 'Frequência de verificação do status das ordens abertas.')" class="info-btn">ⓘ</button></label>
              <input v-model.number="settings.orderPollSeconds" type="number" min="5" class="input" />
            </div>
            <div>
              <label class="field-label">Poll Saldo (s) <button type="button" @click="showInfo('Poll de Saldo', 'Frequência de atualização do saldo disponível.')" class="info-btn">ⓘ</button></label>
              <input v-model.number="settings.balancePollSeconds" type="number" min="10" class="input" />
            </div>
          </div>
        </div>

        <!-- Modo de Operação -->
        <div class="section-card">
          <div class="section-header">⚙️ Modo de Operação</div>
          <div class="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="field-label">Modo de Teste <button type="button" @click="showInfo('Modo de Teste', 'Quando ativo, o bot simula operações sem executar ordens reais na Polymarket.')" class="info-btn">ⓘ</button></label>
              <button type="button" @click="settings.testMode = !settings.testMode" class="toggle-card w-full mt-1.5">
                <span class="text-sm">Simular sem ordens reais</span>
                <span class="text-sm font-bold" :class="settings.testMode ? 'text-blue-400' : 'text-gray-500'">{{ settings.testMode ? '✓ Ativo' : 'Desligado' }}</span>
              </button>
            </div>
            <div>
              <label class="field-label">Modo Debug <button type="button" @click="showInfo('Modo Debug', 'Exibe logs detalhados de todas as operações do bot.')" class="info-btn">ⓘ</button></label>
              <button type="button" @click="settings.debug = !settings.debug" class="toggle-card w-full mt-1.5">
                <span class="text-sm">Logs detalhados</span>
                <span class="text-sm font-bold" :class="settings.debug ? 'text-blue-400' : 'text-gray-500'">{{ settings.debug ? '✓ Ativo' : 'Desligado' }}</span>
              </button>
            </div>
          </div>
        </div>

        <div class="flex gap-4">
          <button type="submit" class="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-3 rounded-xl text-sm font-semibold">Salvar</button>
        </div>
      </form>

      <!-- Console -->
      <div class="section-card">
        <div class="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
          <div>
            <h3 class="text-sm font-semibold text-white">📟 Console do Bot</h3>
            <p class="text-xs text-gray-500 mt-0.5">
              <span v-if="botLogs.logFile">{{ botLogs.logFile }}</span>
              <span v-if="botStatus.pid" class="ml-2">PID: {{ botStatus.pid }}</span>
              <span v-if="botStatus.error" class="ml-2 text-red-400">{{ botStatus.error }}</span>
            </p>
          </div>
          <div class="flex items-center gap-2">
            <div class="flex bg-gray-800 rounded-lg p-0.5 text-xs">
              <button type="button" @click="consoleTab = 'all'"
                class="px-3 py-1 rounded-md transition-all font-medium"
                :class="consoleTab === 'all' ? 'bg-gray-600 text-white' : 'text-gray-400 hover:text-gray-200'">
                Tudo
              </button>
              <button type="button" @click="consoleTab = 'alerts'"
                class="px-3 py-1 rounded-md transition-all font-medium flex items-center gap-1"
                :class="consoleTab === 'alerts' ? 'bg-yellow-500/30 text-yellow-300' : 'text-gray-400 hover:text-gray-200'">
                ⚠ Alertas
              </button>
            </div>
            <button type="button" @click="loadBotLogs" class="action-yellow">Atualizar</button>
          </div>
        </div>
        <div class="p-4">
          <div class="worker-console max-h-96 overflow-auto rounded-xl border border-slate-800">
            <div v-for="(line, i) in displayLogLines" :key="i" class="console-line" :class="logLineClass(line)">{{ line }}</div>
            <div v-if="!displayLogLines.length" class="console-line console-empty">
              {{ consoleTab === 'alerts' ? 'Nenhum alerta registrado.' : 'Sem logs ainda.' }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── TAB: USUÁRIOS (admin only) ───────────────────── -->
    <div v-if="authStore.user?.role === 'admin'" v-show="activeTab === 'users'">
      <div class="section-card">
        <div class="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
          <div>
            <h3 class="text-sm font-semibold text-white">👥 Gestão de Usuários</h3>
            <p class="text-xs text-gray-500 mt-0.5">Criar, editar e remover usuários do sistema.</p>
          </div>
          <button type="button" @click="toggleNewUserForm" class="action-yellow">
            {{ showNewUserForm ? 'Cancelar' : '+ Novo Usuário' }}
          </button>
        </div>

        <!-- New user form -->
        <div v-if="showNewUserForm" class="px-6 py-4 border-b border-gray-800 bg-gray-800/20">
          <h4 class="text-sm font-semibold text-white mb-4">Criar Novo Usuário</h4>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label class="field-label">Username</label>
              <input v-model="newUser.username" type="text" class="input" placeholder="nome_usuario" />
            </div>
            <div>
              <label class="field-label">Email</label>
              <input v-model="newUser.email" type="email" class="input" placeholder="email@exemplo.com" />
            </div>
            <div>
              <label class="field-label">Senha</label>
              <input v-model="newUser.password" type="password" class="input" placeholder="Senha forte..." />
            </div>
            <div>
              <label class="field-label">Perfil</label>
              <select v-model="newUser.role" class="input">
                <option value="user">Usuário</option>
                <option value="admin">Administrador</option>
              </select>
            </div>
          </div>
          <button type="button" @click="createUser" class="bg-gradient-to-r from-blue-600 to-cyan-500 px-6 py-2.5 rounded-xl text-sm font-semibold transition-all">
            Criar Usuário
          </button>
        </div>

        <!-- Users table -->
        <div class="p-6">
          <div v-if="adminUsersLoading" class="text-sm text-gray-500 text-center py-8">Carregando usuários...</div>
          <div v-else-if="adminUsers.length === 0" class="text-sm text-gray-500 text-center py-8">Nenhum usuário encontrado.</div>
          <div v-else class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b border-gray-800">
                  <th class="text-left text-xs font-medium text-gray-400 uppercase tracking-wider pb-3 pr-4">Username</th>
                  <th class="text-left text-xs font-medium text-gray-400 uppercase tracking-wider pb-3 pr-4">Email</th>
                  <th class="text-left text-xs font-medium text-gray-400 uppercase tracking-wider pb-3 pr-4">Perfil</th>
                  <th class="text-left text-xs font-medium text-gray-400 uppercase tracking-wider pb-3 pr-4">Último Login</th>
                  <th class="text-left text-xs font-medium text-gray-400 uppercase tracking-wider pb-3">Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="u in adminUsers" :key="u.user_id || u.username" class="border-b border-gray-800/50 hover:bg-gray-800/20 transition">
                  <td class="py-3 pr-4 text-white font-medium">{{ u.username }}</td>
                  <td class="py-3 pr-4 text-gray-400 text-xs">{{ u.email || '-' }}</td>
                  <td class="py-3 pr-4">
                    <span class="px-2 py-0.5 rounded text-xs font-bold" :class="u.role === 'admin' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-700 text-gray-300'">
                      {{ u.role }}
                    </span>
                  </td>
                  <td class="py-3 pr-4 text-gray-500 text-xs">{{ u.last_login ? new Date(u.last_login).toLocaleString('pt-BR') : '-' }}</td>
                  <td class="py-3">
                    <button
                      v-if="u.username !== authStore.user?.username"
                      type="button"
                      @click="deleteUser(u)"
                      class="text-red-400 hover:text-red-300 text-xs font-medium transition"
                    >Excluir</button>
                    <span v-else class="text-gray-600 text-xs">Você</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- ── TAB: WEATHER MODULE ─────────────────────────── -->
    <div v-show="activeTab === 'weather'">
      <div class="space-y-4">
        <div class="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
          <div class="px-6 py-4 border-b border-gray-800 bg-gray-800/20 flex items-center justify-between">
            <h3 class="text-sm font-semibold text-white">Weather Module</h3>
            <button @click="weatherConfig.weatherEnabled = !weatherConfig.weatherEnabled; saveWeatherConfig()"
              :class="weatherConfig.weatherEnabled ? 'bg-teal-500' : 'bg-gray-600'"
              class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors">
              <span :class="weatherConfig.weatherEnabled ? 'translate-x-6' : 'translate-x-1'"
                class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform" />
            </button>
          </div>

          <div v-if="weatherConfig.weatherEnabled" class="p-6 space-y-5">
            <!-- Budget -->
            <div>
              <label class="text-sm font-medium text-gray-300">Budget Weather / Crypto</label>
              <div class="flex items-center gap-3 mt-2">
                <span class="text-xs text-blue-400 w-16">Crypto {{ 100 - weatherConfig.weatherBudgetPercent }}%</span>
                <input type="range" v-model.number="weatherConfig.weatherBudgetPercent" min="5" max="80" step="5"
                  class="flex-1 h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-teal-500">
                <span class="text-xs text-teal-400 w-16 text-right">Weather {{ weatherConfig.weatherBudgetPercent }}%</span>
              </div>
            </div>

            <!-- API Key -->
            <div>
              <label class="text-sm font-medium text-gray-300">WeatherAPI Key</label>
              <input v-model="weatherConfig.weatherapiKey" type="password"
                placeholder="Chave da API WeatherAPI.com"
                class="mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white" />
            </div>

            <!-- Cidades por continente -->
            <div>
              <div class="flex items-center justify-between mb-2">
                <label class="text-sm font-medium text-gray-300">Cidades Monitoradas</label>
                <span class="text-xs bg-teal-500/20 text-teal-400 px-2 py-0.5 rounded-full">{{ selectedCities.length }} selecionadas</span>
              </div>
              <input v-model="citySearch" placeholder="Buscar cidade ou pais..."
                class="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white mb-2 focus:outline-none focus:border-teal-500" />

              <!-- Continentes colapsáveis -->
              <div class="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden max-h-[320px] overflow-y-auto">
                <template v-for="(group, continent) in cityGroups" :key="continent">
                  <div v-if="group.length > 0">
                    <!-- Header do continente -->
                    <button @click="toggleContinent(continent)"
                      class="w-full flex items-center justify-between px-3 py-2 bg-gray-750 border-b border-gray-700 hover:bg-gray-700/50 sticky top-0 z-10"
                      style="background: rgb(31,41,55);">
                      <div class="flex items-center gap-2">
                        <span class="text-xs">{{ continentEmoji[continent] || '' }}</span>
                        <span class="text-xs font-semibold text-gray-300">{{ continent }}</span>
                        <span class="text-[10px] text-gray-500">({{ group.length }})</span>
                      </div>
                      <div class="flex items-center gap-2">
                        <span class="text-[10px] text-teal-400">{{ group.filter(c => selectedCities.includes(c.key)).length }} sel.</span>
                        <span class="text-gray-500 text-xs">{{ openContinents[continent] ? '&#9660;' : '&#9654;' }}</span>
                      </div>
                    </button>
                    <!-- Cidades do continente -->
                    <div v-show="openContinents[continent]" class="divide-y divide-gray-700/50">
                      <label v-for="city in group" :key="city.key"
                        class="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-700/30 cursor-pointer">
                        <input type="checkbox" :value="city.key" v-model="selectedCities"
                          class="rounded border-gray-600 bg-gray-700 text-teal-500 focus:ring-teal-500 focus:ring-offset-0 h-3.5 w-3.5" />
                        <span class="text-sm text-white flex-1">{{ city.name }}</span>
                        <span class="text-[10px] text-gray-500">{{ city.country }}</span>
                        <span v-if="city.tier === 1" class="text-[9px] bg-teal-500/20 text-teal-400 px-1.5 py-0.5 rounded">TOP</span>
                        <span v-else-if="city.tier === 2" class="text-[9px] bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">NOAA</span>
                      </label>
                    </div>
                  </div>
                </template>
              </div>
            </div>

            <!-- Edge + Max bets -->
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="text-sm font-medium text-gray-300">Edge Minimo (%)</label>
                <input v-model.number="weatherConfig.weatherMinEdge" type="number" step="1" min="1" max="20"
                  class="mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white" />
              </div>
              <div>
                <label class="text-sm font-medium text-gray-300">Max Apostas</label>
                <input v-model.number="weatherConfig.weatherMaxBets" type="number" step="1" min="1" max="10"
                  class="mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white" />
              </div>
            </div>

            <!-- Botão salvar com loading e feedback -->
            <button @click="saveWeatherConfig()" :disabled="weatherSaving"
              class="w-full bg-teal-600 hover:bg-teal-500 disabled:bg-teal-600/50 disabled:cursor-wait text-white rounded-lg py-2.5 text-sm font-medium transition-colors flex items-center justify-center gap-2">
              <svg v-if="weatherSaving" class="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
              <span>{{ weatherSaving ? 'Salvando...' : 'Salvar Configuracoes Weather' }}</span>
            </button>
            <Transition name="fade">
              <div v-if="weatherSaveMsg" class="text-center text-sm font-medium rounded-lg py-2"
                :class="weatherSaveOk ? 'text-teal-400 bg-teal-500/10' : 'text-red-400 bg-red-500/10'">
                {{ weatherSaveMsg }}
              </div>
            </Transition>
          </div>

          <!-- Desativado -->
          <div v-else class="p-6 text-center text-gray-500 text-sm">
            Modulo desativado. Ative o toggle acima para configurar.
          </div>
        </div>
      </div>
    </div>

    <!-- ── TAB: METAS ─────────────────────────── -->
    <div v-show="activeTab === 'metas'">
      <div class="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
        <div class="px-6 py-4 border-b border-gray-800 bg-gray-800/20">
          <h3 class="text-sm font-semibold text-white">&#128202; Configuracao de Metas</h3>
          <p class="text-xs text-gray-500 mt-1">Defina suas metas diarias e mensais de lucro</p>
        </div>
        <div class="px-6 py-4 space-y-4">
          <div>
            <label class="block text-xs font-medium text-gray-400 mb-1">Meta Diaria (%)</label>
            <input v-model.number="metasConfig.dailyTargetPct" type="number" step="0.5" min="0" max="100"
              class="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent" />
            <p class="text-[10px] text-gray-500 mt-1">Percentual de lucro esperado por dia sobre o saldo</p>
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-400 mb-1">Meta 30 Dias (USD)</label>
            <input v-model.number="metasConfig.monthlyTargetUsd" type="number" step="10" min="0"
              class="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent" />
            <p class="text-[10px] text-gray-500 mt-1">Valor total de lucro esperado em 30 dias. 0 = calculo automatico pela meta diaria</p>
          </div>
          <div>
            <label class="block text-xs font-medium text-gray-400 mb-1">Saldo Inicial de Referencia (USD)</label>
            <input v-model.number="metasConfig.initialBalance" type="number" step="1" min="1"
              class="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent" />
          </div>
          <div class="bg-gray-800/50 rounded-lg p-3">
            <p class="text-xs text-gray-400">Projecao: Se manter {{ metasConfig.dailyTargetPct }}%/dia por 30 dias:</p>
            <p class="text-lg font-bold text-emerald-400 mt-1">${{ (metasConfig.initialBalance * Math.pow(1 + metasConfig.dailyTargetPct/100, 30)).toFixed(2) }}</p>
          </div>
          <div class="flex justify-end gap-2">
            <button @click="saveMetasConfig" class="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg transition">
              {{ metasSaving ? 'Salvando...' : 'Salvar Metas' }}
            </button>
          </div>
          <div v-if="metasMsg" class="text-xs px-3 py-2 rounded-lg" :class="metasMsg.includes('Erro') ? 'bg-red-500/10 text-red-400' : 'bg-emerald-500/10 text-emerald-400'">{{ metasMsg }}</div>
        </div>
      </div>
    </div>

    <!-- ── TAB: ALERTAS WHATSAPP ─────────────────────────── -->
    <div v-show="activeTab === 'notifications'">
      <div class="space-y-4">

        <!-- Telefone -->
        <div class="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
          <div class="px-6 py-4 border-b border-gray-800 bg-gray-800/20">
            <h3 class="text-sm font-semibold text-white">📱 WhatsApp — Configuração</h3>
            <p class="text-xs text-gray-500 mt-0.5">Receba alertas de performance direto no seu WhatsApp.</p>
          </div>
          <div class="p-6 space-y-5">
            <!-- Número -->
            <div>
              <label class="field-label">Telefone WhatsApp <span class="text-gray-600 font-normal">(DDI + DDD + número, somente dígitos)</span></label>
              <input v-model="notif.whatsappPhone" type="tel" class="input w-full"
                placeholder="Ex: 5567991125559"
                @input="notif.phoneError = ''" />
              <p v-if="notif.phoneError" class="mt-1.5 text-xs text-red-400">{{ notif.phoneError }}</p>
              <p class="mt-1.5 text-xs text-gray-600">DDI = código do país (55 Brasil) + DDD + número. Sem espaços, traços ou parênteses.</p>
            </div>

            <!-- Toggles -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="bg-gray-800/60 border border-gray-700/60 rounded-xl p-4 flex items-center justify-between gap-4">
                <div>
                  <p class="text-sm font-medium text-white">⏰ Alerta a cada hora</p>
                  <p class="text-xs text-gray-500 mt-0.5">Receba um resumo a cada hora com saldo, lucro e melhor aposta.</p>
                </div>
                <button type="button" @click="notif.whatsappHourlyEnabled = !notif.whatsappHourlyEnabled"
                  class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200"
                  :class="notif.whatsappHourlyEnabled ? 'bg-green-500' : 'bg-gray-600'">
                  <span class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200"
                    :class="notif.whatsappHourlyEnabled ? 'translate-x-5' : 'translate-x-0'"></span>
                </button>
              </div>
              <div class="bg-gray-800/60 border border-gray-700/60 rounded-xl p-4 flex items-center justify-between gap-4">
                <div>
                  <p class="text-sm font-medium text-white">📅 Resumo diário</p>
                  <p class="text-xs text-gray-500 mt-0.5">Resumo do dia às 8h (GMT-4 Cuiabá), comparando com o dia anterior.</p>
                </div>
                <button type="button" @click="notif.whatsappDailyEnabled = !notif.whatsappDailyEnabled"
                  class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200"
                  :class="notif.whatsappDailyEnabled ? 'bg-green-500' : 'bg-gray-600'">
                  <span class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200"
                    :class="notif.whatsappDailyEnabled ? 'translate-x-5' : 'translate-x-0'"></span>
                </button>
              </div>
            </div>

            <!-- Horário de silêncio -->
            <div class="bg-gray-800/40 border border-gray-700/40 rounded-xl p-4">
              <p class="text-sm font-medium text-white mb-3">🌙 Horário de silêncio <span class="text-xs text-gray-500 font-normal">(não receber alertas neste período — GMT-4)</span></p>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <label class="field-label">Início do silêncio (h)</label>
                  <select v-model.number="notif.whatsappQuietStart" class="input">
                    <option v-for="h in 24" :key="h-1" :value="h-1">{{ String(h-1).padStart(2,'0') }}:00</option>
                  </select>
                </div>
                <div>
                  <label class="field-label">Fim do silêncio (h)</label>
                  <select v-model.number="notif.whatsappQuietEnd" class="input">
                    <option v-for="h in 24" :key="h-1" :value="h-1">{{ String(h-1).padStart(2,'0') }}:00</option>
                  </select>
                </div>
              </div>
              <p class="mt-2 text-xs text-gray-600">Ex: silêncio das 22h às 07h = sem mensagens durante a madrugada.</p>
            </div>

            <!-- Botão salvar tudo -->
            <button type="button" @click="saveNotifications"
              class="w-full bg-gradient-to-r from-green-600 to-emerald-500 px-6 py-3 rounded-xl text-sm font-semibold transition-all hover:opacity-90">
              💾 Salvar Configurações de Alerta
            </button>

            <!-- Mensagem de status -->
            <p v-if="notif.saveMsg" class="text-center text-xs" :class="notif.saveOk ? 'text-green-400' : 'text-red-400'">{{ notif.saveMsg }}</p>
          </div>
        </div>

        <!-- Teste de envio -->
        <div class="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
          <div class="px-6 py-4 border-b border-gray-800 bg-gray-800/20">
            <h3 class="text-sm font-semibold text-white">🧪 Teste de Envio</h3>
            <p class="text-xs text-gray-500 mt-0.5">Envie uma mensagem de teste agora para confirmar que o número está correto.</p>
          </div>
          <div class="p-6 flex flex-wrap gap-3">
            <button type="button" @click="sendTestNotification('hourly')" :disabled="notif.sending"
              class="flex-1 min-w-[160px] px-5 py-3 bg-blue-600/20 border border-blue-500/30 hover:bg-blue-600/30 rounded-xl text-sm font-medium text-blue-300 transition-all disabled:opacity-40">
              ⏰ Enviar alerta horário agora
            </button>
            <button type="button" @click="sendTestNotification('daily')" :disabled="notif.sending"
              class="flex-1 min-w-[160px] px-5 py-3 bg-purple-600/20 border border-purple-500/30 hover:bg-purple-600/30 rounded-xl text-sm font-medium text-purple-300 transition-all disabled:opacity-40">
              📅 Enviar resumo diário agora
            </button>
          </div>
          <p v-if="notif.testMsg" class="px-6 pb-4 text-xs text-center" :class="notif.testOk ? 'text-green-400' : 'text-red-400'">{{ notif.testMsg }}</p>
        </div>

        <!-- Preview das mensagens -->
        <div class="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
          <div class="px-6 py-4 border-b border-gray-800 bg-gray-800/20">
            <h3 class="text-sm font-semibold text-white">👀 Preview das Mensagens</h3>
          </div>
          <div class="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div class="bg-gray-950/60 border border-gray-700/40 rounded-xl p-4">
              <p class="text-xs font-semibold text-blue-400 mb-2">⏰ Alerta Horário</p>
              <pre class="text-xs text-gray-400 whitespace-pre-wrap leading-relaxed font-mono">🤖 *BetIA BitCrypto* — Relatório Horário
🕐 *14:00* (GMT-4 Cuiabá)
━━━━━━━━━━━━━━━━━━━━

👤 *Usuário:* seu_usuario

💼 *Banca Atual:* $45.20
⏮️ *Hora anterior:* $43.80
📈 *Variação última hora:* +1.40 USDC

📊 *Resumo do Dia*
  ✅ Ganhos: 12 apostas
  ❌ Perdas: 5 apostas
  ⏳ Pendentes: 8

💰 *Lucro do Dia:* +3.20 USDC 📈
📈 *Lucro Acumulado:* -74.80 USDC

🏆 *Melhor aposta da hora:*
  _BTC above $72,000 on March 13_
  Lucro: *+4.10 USDC*

━━━━━━━━━━━━━━━━━━━━
_BetIA • Sistema autônomo de trading_</pre>
            </div>
            <div class="bg-gray-950/60 border border-gray-700/40 rounded-xl p-4">
              <p class="text-xs font-semibold text-purple-400 mb-2">📅 Resumo Diário</p>
              <pre class="text-xs text-gray-400 whitespace-pre-wrap leading-relaxed font-mono">🤖 *BetIA BitCrypto* — Resumo Diário
📅 *Quinta-feira, 13 de março de 2026*
━━━━━━━━━━━━━━━━━━━━

👤 *Usuário:* seu_usuario

💼 *Banca Final:* $45.20
⏮️ *Banca Ontem:* $120.00
📉 *Variação do Dia:* -74.80 USDC

📊 *Estatísticas de Hoje*
  ✅ Ganhos: *18* apostas
  ❌ Perdas: *35* apostas
  🎯 Taxa de acerto: *52.9%*

📊 *Estatísticas de Ontem*
  ✅ Ganhos: 115 | ❌ Perdas: 61

💰 *Lucro de Hoje:* -74.80 USDC 📉
📈 *Lucro Acumulado Total:* -74.80 USDC

🏆 *Melhor aposta do dia:*
  _XRP above $1.40 on March 13_
  Lucro: *+4.30 USDC*

━━━━━━━━━━━━━━━━━━━━
_BetIA • Sistema autônomo de trading_</pre>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- ── TAB: AGENTES ──────────────────────────────────── -->
    <div v-show="activeTab === 'agents'">
      <div class="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
        <div class="px-6 py-4 border-b border-gray-800 bg-gray-800/20 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h3 class="text-sm font-semibold text-white">🤖 Agentes de Mercado</h3>
            <p class="text-xs text-gray-500 mt-0.5">Cada agente atua numa janela de tempo diferente. Janelas não podem se sobrepor. Os pesos são relativos — o bot distribui o capital proporcionalmente entre os agentes ativos.</p>
          </div>
          <button @click="addAgent" :disabled="agents.length >= agentLimits.max_agents" class="action-green text-xs px-3 py-1.5">
            + Novo Agente
          </button>
        </div>

        <!-- Erro de validação -->
        <div v-if="agentsError" class="mx-6 mt-4 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-xl text-xs text-red-400">
          {{ agentsError }}
        </div>

        <!-- Cabeçalho da tabela -->
        <div v-if="agents.length" class="hidden md:grid grid-cols-12 gap-2 px-6 pt-4 pb-1 text-xs text-gray-600 uppercase tracking-wider font-semibold">
          <div class="col-span-3">Nome</div>
          <div class="col-span-2">Janela (min antes fechamento)</div>
          <div class="col-span-2">Categorias</div>
          <div class="col-span-1 text-center">Peso</div>
          <div class="col-span-1 text-center">Conf. min %</div>
          <div class="col-span-1 text-center">Stop %</div>
          <div class="col-span-1 text-center">Ativo</div>
          <div class="col-span-1 text-center"></div>
        </div>

        <!-- Cards de agentes -->
        <div class="p-6 space-y-3">
          <div v-if="!agents.length" class="text-center py-10 text-gray-600 text-sm">
            Nenhum agente configurado. Clique em "+ Novo Agente" para começar.
          </div>

          <div v-for="(agent, idx) in agents" :key="agent._key"
            class="grid grid-cols-12 gap-2 items-center p-3 bg-gray-800/40 border border-gray-700/50 rounded-xl"
            :class="{ 'opacity-50': !agent.is_active }">

            <!-- Nome -->
            <div class="col-span-12 md:col-span-3">
              <input v-model="agent.name" class="input text-xs h-8" placeholder="Nome do agente" />
            </div>

            <!-- Janela -->
            <div class="col-span-12 md:col-span-2 flex gap-1 items-center">
              <input v-model.number="agent.window_start_minutes" type="number" min="0" class="input text-xs h-8 w-16 text-center" @change="validateAgents" />
              <span class="text-gray-600 text-xs">→</span>
              <input v-model.number="agent.window_end_minutes" type="number" min="1" class="input text-xs h-8 w-16 text-center" @change="validateAgents" />
            </div>

            <!-- Categorias -->
            <div class="col-span-12 md:col-span-2">
              <div class="flex gap-1 flex-wrap">
                <label v-for="cat in availableCategories" :key="cat.id"
                  class="cursor-pointer px-1.5 py-0.5 rounded border text-[10px] transition-colors select-none"
                  :class="agent.market_categories.includes(cat.id)
                    ? 'bg-blue-500/20 border-blue-500/40 text-blue-300'
                    : 'border-gray-700/50 text-gray-600 hover:text-gray-400 hover:border-gray-600'">
                  <input type="checkbox" :value="cat.id" v-model="agent.market_categories" class="sr-only" @change="validateAgents" />
                  {{ cat.label }}
                </label>
              </div>
            </div>

            <!-- Peso -->
            <div class="col-span-4 md:col-span-1 text-center">
              <input v-model.number="agent.portfolio_weight" type="number" min="1" max="100" class="input text-xs h-8 text-center" />
            </div>

            <!-- Confiança mínima -->
            <div class="col-span-4 md:col-span-1 text-center">
              <input v-model.number="agent.min_confidence" type="number" min="50" max="99" class="input text-xs h-8 text-center" />
            </div>

            <!-- Stop loss -->
            <div class="col-span-4 md:col-span-1 text-center">
              <input v-model.number="agent.stop_loss_percent" type="number" min="1" max="50" class="input text-xs h-8 text-center" />
            </div>

            <!-- Toggle ativo -->
            <div class="col-span-6 md:col-span-1 flex justify-center">
              <button @click="agent.is_active = !agent.is_active; validateAgents()"
                class="w-10 h-5 rounded-full transition-colors relative flex-shrink-0"
                :class="agent.is_active ? 'bg-green-500' : 'bg-gray-600'">
                <span class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200"
                  :class="agent.is_active ? 'translate-x-5' : 'translate-x-0'"></span>
              </button>
            </div>

            <!-- Remover -->
            <div class="col-span-6 md:col-span-1 flex justify-center">
              <button @click="removeAgent(idx)" class="text-gray-600 hover:text-red-400 transition text-lg leading-none">×</button>
            </div>
          </div>

          <!-- Resumo de pesos -->
          <div v-if="agents.filter(a => a.is_active).length" class="flex gap-4 flex-wrap pt-2 text-xs text-gray-500">
            <span v-for="agent in agents.filter(a => a.is_active)" :key="agent._key">
              <span class="text-white font-medium">{{ agent.name || 'Agente' }}</span>:
              {{ agentEffectivePct(agent).toFixed(1) }}% carteira
              ({{ agent.window_start_minutes }}–{{ agent.window_end_minutes }}min)
            </span>
          </div>
        </div>

        <!-- Salvar -->
        <div class="px-6 pb-5 flex items-center gap-4">
          <button @click="saveAgents" :disabled="!!agentsError || agentsSaving" class="action-green px-5 py-2 text-xs disabled:opacity-40">
            {{ agentsSaving ? 'Salvando...' : 'Salvar Agentes' }}
          </button>
          <span v-if="agentsSaveMsg" class="text-xs" :class="agentsSaveOk ? 'text-green-400' : 'text-red-400'">{{ agentsSaveMsg }}</span>
          <span class="text-xs text-gray-600 ml-auto">{{ agents.filter(a=>a.is_active).length }}/{{ agentLimits.max_agents }} agentes ativos</span>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useAuthStore } from '../stores/authStore';
import { authService } from '../services/authService';
import { getApiV1BaseUrl } from '../config/runtime';

const authStore = useAuthStore();

// Active tab
const activeTab = ref('credentials');
const tabs = [
  { id: 'credentials',   label: 'Credenciais', icon: '🔑' },
  { id: 'trading',       label: 'Negociação',  icon: '💰' },
  { id: 'markets',       label: 'Mercados',    icon: '🎯' },
  { id: 'agents',        label: 'Agentes',     icon: '🤖' },
  { id: 'protection',    label: 'Proteção',    icon: '🛡️' },
  { id: 'bot',           label: 'Bot & Logs',  icon: '⚙️' },
  { id: 'weather',       label: 'Weather',     icon: '🌤️' },
  { id: 'metas',         label: 'Metas',       icon: '📊' },
  { id: 'notifications', label: 'Alertas',     icon: '🔔' },
  { id: 'users',         label: 'Usuários',    icon: '👥', adminOnly: true },
];

// Metas config
const metasConfig = ref({ dailyTargetPct: 5, monthlyTargetUsd: 0, initialBalance: 100 });
const metasSaving = ref(false);
const metasMsg = ref('');

async function loadMetasConfig() {
  try {
    const token = localStorage.getItem('token');
    const r = await fetch(`${getApiV1BaseUrl()}/metas/config`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) { const d = await r.json(); Object.assign(metasConfig.value, d); }
  } catch (_) {}
}

async function saveMetasConfig() {
  metasSaving.value = true; metasMsg.value = '';
  try {
    const token = localStorage.getItem('token');
    const r = await fetch(`${getApiV1BaseUrl()}/metas/config`, {
      method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(metasConfig.value),
    });
    metasMsg.value = r.ok ? 'Metas salvas com sucesso!' : 'Erro ao salvar';
  } catch (e) { metasMsg.value = 'Erro: ' + e.message; }
  metasSaving.value = false;
  setTimeout(() => { metasMsg.value = ''; }, 3000);
}

// Info modal
const infoModal = ref({ show: false, title: '', description: '' });
function showInfo(title, description) {
  infoModal.value = { show: true, title, description };
}

const credentials = ref({
  polymarket_private_key: '',
  polymarket_funder_address: '',
  gemini_api_key: '',
  coingecko_api_key: '',
  initial_balance: 100,
});

const settings = ref({
  initialBalance: 100,
  defaultBetSize: 3,
  minConfidenceThreshold: 65,
  maxRiskPerTrade: 8,
  profitClaimThreshold: 20,
  bankGrowthMultiplier: 1.1,
  analysisIntervalMinutes: 5,
  orderTimeoutMinutes: 30,
  analysisPollSeconds: 10,
  claimPollSeconds: 5,
  orderPollSeconds: 5,
  balancePollSeconds: 30,
  maxHoursToExpiry: 24,
  edgeMinPrice: 0.05,
  edgeMaxPrice: 0.99,
  coinflipMinPrice: 0.05,
  coinflipMaxPrice: 0.50,
  maxAnalysisMarkets: 40,
  maxConcurrentPositions: 30,
  maxOpenExposurePercent: 30,
  dailyStopLossUsd: 10,
  maxPeakDrawdownPercent: 18,
  stopLossPercent: 10,
  criticalStopPrice: 0.18,
  lateStageStopSeconds: 3600,
  heroCheckpointEnabled: true,
  heroActivationProfitPercent: 100,
  heroRetracePercent: 10,
  conservativePoolPercent: 50,
  moderatePoolPercent: 30,
  extremePoolPercent: 20,
  conservativeMinBet: 3.0,
  moderateMinBet: 2.0,
  extremeMinBet: 1.0,
  extremeMinConfidence: 68,
  closeWindowMinutes: 3,
  testMode: false,
  debug: false,
});

// Notificações WhatsApp
const notif = ref({
  whatsappPhone: '',
  whatsappHourlyEnabled: false,
  whatsappDailyEnabled: false,
  whatsappQuietStart: 22,
  whatsappQuietEnd: 7,
  cooldownEnabled: true,
  cooldownMinLosses: 3,
  cooldownMinLossUsdc: 5.0,
  cooldownMinutes: 240,
  phoneError: '',
  saveMsg: '',
  saveOk: false,
  testMsg: '',
  testOk: false,
  sending: false,
});

const runtimeProfile = ref({ currentProfile: null, activeProfile: null, defaults: null });
const botStatus = ref({ isRunning: false, activeUsername: null });
const botLogs = ref({ lines: [], logFile: null });

const adminUsers = ref([]);
const adminUsersLoading = ref(false);
const showNewUserForm = ref(false);
const newUser = ref({ username: '', email: '', password: '', role: 'user' });

// ── AGENTES ──────────────────────────────────────────────────────────────────
const agents = ref([]);
const agentLimits = ref({ max_agents: 3 });
const agentsError = ref('');
const agentsSaving = ref(false);
const agentsSaveMsg = ref('');
const agentsSaveOk = ref(false);
const availableCategories = ref([
  { id: 'crypto', label: 'Crypto' },
  { id: 'clima', label: 'Clima' },
  { id: 'politica', label: 'Política' },
  { id: 'outros', label: 'Outros' },
]);

let _agentKeyCounter = 0;
function mkKey() { return ++_agentKeyCounter; }

async function loadAgents() {
  try {
    const res = await authService.getAgents();
    agentLimits.value = res.limits || { max_agents: 3 };
    agents.value = (res.agents || []).map(a => ({ ...a, _key: mkKey() }));
  } catch { /* silent */ }
}

function addAgent() {
  if (agents.value.length >= agentLimits.value.max_agents + 5) return; // allow drafts
  agents.value.push({
    _key: mkKey(),
    name: '',
    market_categories: ['crypto', 'clima'],
    window_start_minutes: 0,
    window_end_minutes: 120,
    portfolio_weight: 33,
    min_confidence: 65,
    stop_loss_percent: 10,
    is_active: true,
  });
  validateAgents();
}

function removeAgent(idx) {
  agents.value.splice(idx, 1);
  validateAgents();
}

function validateAgents() {
  const active = agents.value.filter(a => a.is_active);
  for (let i = 0; i < active.length; i++) {
    const a = active[i];
    if ((a.window_start_minutes ?? 0) >= (a.window_end_minutes ?? 0)) {
      agentsError.value = `"${a.name || 'Agente'}": início da janela deve ser menor que o fim`;
      return;
    }
    for (let j = i + 1; j < active.length; j++) {
      const b = active[j];
      const overlaps = !(a.window_end_minutes <= b.window_start_minutes || b.window_end_minutes <= a.window_start_minutes);
      if (overlaps) {
        agentsError.value = `Janelas sobrepostas: "${a.name || 'Agente'}" (${a.window_start_minutes}–${a.window_end_minutes}min) e "${b.name || 'Agente'}" (${b.window_start_minutes}–${b.window_end_minutes}min)`;
        return;
      }
    }
  }
  agentsError.value = '';
}

function agentEffectivePct(agent) {
  const totalWeight = agents.value.filter(a => a.is_active).reduce((s, a) => s + (a.portfolio_weight || 0), 0) || 1;
  return ((agent.portfolio_weight || 0) / totalWeight) * 100;
}

async function saveAgents() {
  validateAgents();
  if (agentsError.value) return;
  agentsSaving.value = true;
  agentsSaveMsg.value = '';
  try {
    const res = await authService.saveAgents(agents.value.map(({ _key, ...a }) => a));
    if (res.success) {
      agents.value = (res.agents || []).map(a => ({ ...a, _key: mkKey() }));
      agentsSaveOk.value = true;
      agentsSaveMsg.value = 'Agentes salvos!';
    } else {
      agentsSaveOk.value = false;
      agentsSaveMsg.value = res.error || 'Erro ao salvar';
    }
  } catch (e) {
    agentsSaveOk.value = false;
    agentsSaveMsg.value = e?.response?.data?.error || 'Erro ao salvar';
  } finally {
    agentsSaving.value = false;
    setTimeout(() => { agentsSaveMsg.value = ''; }, 3000);
  }
}

let refreshTimer = null;

function notify(message, type = 'info') {
  window.dispatchEvent(new CustomEvent('app:notify', { detail: { message, type } }));
}

function decodeMojibake(value) {
  const text = String(value || '');
  if (!text || !/[ÃÂ\uFFFD]/.test(text)) return text;
  try {
    const bytes = Uint8Array.from(text.split('').map(c => c.charCodeAt(0) & 0xff));
    return new TextDecoder('utf-8', { fatal: false }).decode(bytes);
  } catch { return text; }
}

function stripAnsi(value) {
  return String(value || '').replace(/\x1b\[[0-9;]*m/g, '');
}

function logLineClass(line) {
  const n = stripAnsi(line).toUpperCase();
  if (n.includes(' ERROR ') || n.includes('ERRO')) return 'console-error';
  if (n.includes('[HERO]') || n.includes('HERO CHECKPOINT')) return 'console-hero';
  if (n.includes('[WIN]') || n.includes('GANHOU!')) return 'console-win';
  if (n.includes('[LOSS]') || n.includes('PERDEU!')) return 'console-loss';
  if (n.includes('[RISK]') || n.includes('DRAWDOWN') || n.includes('EXPOSICAO ABERTA NO LIMITE')) return 'console-risk';
  if (n.includes('[STATE]') || n.includes('BOT PRONTO') || n.includes('PERFIL ATIVO')) return 'console-state';
  if (n.includes(' WARNING ') || n.includes('AVISO')) return 'console-warning';
  if (n.includes('[BUY]') || n.includes(' BUY ') || n.includes('ORDEM REAL') || n.includes('EXECUTADO')) return 'console-buy';
  if (n.includes(' STOP LOSS ') || n.includes(' SELL ')) return 'console-sell';
  if (n.includes('[REDEEM]') || n.includes(' REDEEM ') || n.includes('RESGATE')) return 'console-redeem';
  if (n.includes('[GEMINI]') || n.includes(' GEMINI ') || n.includes('ANALISANDO ')) return 'console-gemini';
  return 'console-info';
}

function mergeConfig(config, defaults) {
  return { ...(defaults || {}), ...(config || {}) };
}

async function loadSettings() {
  try {
    const [credentialsResponse, configResponse, runtimeResponse, statusResponse] = await Promise.all([
      authService.getCredentials(),
      authService.getConfig(),
      authService.getRuntimeProfile(),
      authService.getBotStatus(),
    ]);
    if (credentialsResponse?.credentials) {
      credentials.value = {
        ...credentials.value,
        ...credentialsResponse.credentials,
        polymarket_private_key: '',
        gemini_api_key: '',
        coingecko_api_key: '',
      };
    }
    runtimeProfile.value = runtimeResponse || runtimeProfile.value;
    settings.value = mergeConfig(configResponse?.config, runtimeResponse?.defaults);
    if (statusResponse?.status) botStatus.value = statusResponse.status;
  } catch (error) {
    console.error('Erro ao carregar configurações:', error);
    notify('Erro ao carregar configurações', 'error');
  }
}

async function loadBotLogs() {
  try {
    const logsResponse = await authService.getBotLogs(200);
    botLogs.value = {
      ...(logsResponse || botLogs.value),
      lines: (logsResponse?.lines || []).map(l => stripAnsi(decodeMojibake(l))),
    };
  } catch (error) { console.error('Erro ao carregar logs:', error); }
}

async function refreshBotState() {
  try {
    const statusResponse = await authService.getBotStatus();
    if (statusResponse?.status) botStatus.value = statusResponse.status;
    await loadBotLogs();
  } catch (error) { console.error('Erro ao atualizar estado:', error); }
}

async function saveCredentials() {
  try {
    await authService.setCredentials(credentials.value);
    await loadSettings();
    notify('Credenciais salvas com sucesso', 'success');
  } catch (error) {
    notify(error.response?.data?.error || 'Erro ao salvar credenciais', 'error');
  }
}

async function saveSettings() {
  try {
    await authService.updateConfig(settings.value);
    await authService.setCredentials({ initial_balance: settings.value.initialBalance });
    await loadSettings();
    notify('Configurações salvas com sucesso', 'success');
  } catch (error) {
    notify(error.response?.data?.error || 'Erro ao salvar configurações', 'error');
  }
}

function resetDefaults() {
  settings.value = mergeConfig({}, runtimeProfile.value.defaults);
}

async function startBot() {
  try {
    botLogs.value = { ...botLogs.value, lines: [] };
    const response = await authService.startBot();
    botStatus.value = response.status;
    await loadBotLogs();
    notify(response.message, 'success');
  } catch (error) {
    notify(error.response?.data?.error || 'Erro ao iniciar bot', 'error');
  }
}

async function stopBot() {
  try {
    const response = await authService.stopBot();
    botStatus.value = response.status;
    notify(response.message, 'info');
  } catch (error) {
    notify(error.response?.data?.error || 'Erro ao parar bot', 'error');
  }
}

async function restartBot() {
  try {
    botLogs.value = { ...botLogs.value, lines: [] };
    const response = await authService.restartBot();
    botStatus.value = response.status;
    await loadBotLogs();
    notify(response.message, 'success');
  } catch (error) {
    notify(error.response?.data?.error || 'Erro ao reiniciar bot', 'error');
  }
}

async function loadAdminUsers() {
  if (authStore.user?.role !== 'admin') return;
  adminUsersLoading.value = true;
  try {
    const res = await fetch(`${getApiV1BaseUrl()}/admin/users`, {
      headers: { Authorization: `Bearer ${authStore.token}` },
    });
    if (!res.ok) throw new Error('Falha ao carregar usuários');
    const data = await res.json();
    adminUsers.value = data.users || data || [];
  } catch (error) {
    notify('Erro ao carregar lista de usuários', 'error');
  } finally {
    adminUsersLoading.value = false;
  }
}

function toggleNewUserForm() {
  showNewUserForm.value = !showNewUserForm.value;
  if (!showNewUserForm.value) newUser.value = { username: '', email: '', password: '', role: 'user' };
}

async function createUser() {
  if (!newUser.value.username || !newUser.value.password) {
    notify('Username e senha são obrigatórios', 'error');
    return;
  }
  try {
    const res = await fetch(`${getApiV1BaseUrl()}/admin/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authStore.token}` },
      body: JSON.stringify(newUser.value),
    });
    if (!res.ok) { const err = await res.json(); throw new Error(err.error || 'Erro ao criar usuário'); }
    notify('Usuário criado com sucesso', 'success');
    showNewUserForm.value = false;
    newUser.value = { username: '', email: '', password: '', role: 'user' };
    await loadAdminUsers();
  } catch (error) {
    notify(error.message || 'Erro ao criar usuário', 'error');
  }
}

async function deleteUser(u) {
  const confirmed = window.confirm(`Tem certeza que deseja excluir o usuário "${u.username}"?`);
  if (!confirmed) return;
  try {
    // Use user_id (numeric) — not u.id which may be undefined
    const id = u.user_id || u.id;
    if (!id) throw new Error('ID do usuário não encontrado');
    const res = await fetch(`/api/v1/admin/users/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${authStore.token}` },
    });
    if (!res.ok) { const err = await res.json(); throw new Error(err.error || 'Erro ao excluir usuário'); }
    notify(`Usuário "${u.username}" excluído`, 'info');
    await loadAdminUsers();
  } catch (error) {
    notify(error.message || 'Erro ao excluir usuário', 'error');
  }
}

const consoleTab = ref('all');
const MAX_CONSOLE_LINES = 500;

const ALERT_CLASSES = new Set(['console-error','console-hero','console-win','console-loss','console-risk','console-state','console-warning','console-redeem','console-buy','console-sell']);

const displayLogLines = computed(() => {
  const lines = (botLogs.value?.lines || []).slice(-MAX_CONSOLE_LINES);
  if (consoleTab.value === 'alerts') {
    return lines.filter(l => ALERT_CLASSES.has(logLineClass(l)));
  }
  return lines;
});

// ── Notificações WhatsApp ────────────────────────────────────────────────────
async function loadNotifications() {
  try {
    const base = getApiV1BaseUrl();
    const headers = authStore.token ? { Authorization: `Bearer ${authStore.token}` } : {};
    const res = await fetch(`${base}/user/notifications`, { headers });
    if (res.ok) {
      const data = await res.json();
      const n = data.notifications || {};
      notif.value.whatsappPhone         = n.whatsappPhone        || '';
      notif.value.whatsappHourlyEnabled  = Boolean(n.whatsappHourlyEnabled);
      notif.value.whatsappDailyEnabled   = Boolean(n.whatsappDailyEnabled);
      notif.value.whatsappQuietStart     = n.whatsappQuietStart  ?? 22;
      notif.value.whatsappQuietEnd       = n.whatsappQuietEnd    ?? 7;
      notif.value.cooldownEnabled        = n.cooldownEnabled     !== false;
      notif.value.cooldownMinLosses      = n.cooldownMinLosses   ?? 3;
      notif.value.cooldownMinLossUsdc    = n.cooldownMinLossUsdc ?? 5.0;
      notif.value.cooldownMinutes        = n.cooldownMinutes     ?? 240;
    }
  } catch (e) { console.error('Erro ao carregar notificações:', e); }
}

async function saveNotifications() {
  notif.value.phoneError = '';
  notif.value.saveMsg    = '';
  const phone = notif.value.whatsappPhone.replace(/\D/g, '');
  if (phone && (phone.length < 10 || phone.length > 15)) {
    notif.value.phoneError = 'Telefone inválido. Informe DDI+DDD+número, somente dígitos (10-15 caracteres). Ex: 5567991125559';
    return;
  }
  try {
    const base = getApiV1BaseUrl();
    const headers = { 'Content-Type': 'application/json', ...(authStore.token ? { Authorization: `Bearer ${authStore.token}` } : {}) };
    const res = await fetch(`${base}/user/notifications`, {
      method: 'PUT', headers,
      body: JSON.stringify({
        whatsappPhone:         phone || '',
        whatsappHourlyEnabled: notif.value.whatsappHourlyEnabled,
        whatsappDailyEnabled:  notif.value.whatsappDailyEnabled,
        whatsappQuietStart:    notif.value.whatsappQuietStart,
        whatsappQuietEnd:      notif.value.whatsappQuietEnd,
        cooldownEnabled:       notif.value.cooldownEnabled,
        cooldownMinLosses:     notif.value.cooldownMinLosses,
        cooldownMinLossUsdc:   notif.value.cooldownMinLossUsdc,
        cooldownMinutes:       notif.value.cooldownMinutes,
      }),
    });
    const data = await res.json();
    if (res.ok) {
      notif.value.saveMsg = '✅ Configurações salvas com sucesso!';
      notif.value.saveOk  = true;
    } else {
      notif.value.saveMsg = `❌ ${data.error || 'Erro ao salvar'}`;
      notif.value.saveOk  = false;
    }
  } catch (e) {
    notif.value.saveMsg = `❌ Erro: ${e.message}`;
    notif.value.saveOk  = false;
  }
  setTimeout(() => { notif.value.saveMsg = ''; }, 5000);
}

async function sendTestNotification(type) {
  notif.value.testMsg  = '';
  notif.value.sending  = true;
  try {
    const base = getApiV1BaseUrl();
    const headers = { 'Content-Type': 'application/json', ...(authStore.token ? { Authorization: `Bearer ${authStore.token}` } : {}) };
    const res = await fetch(`${base}/notifications/test`, {
      method: 'POST', headers,
      body: JSON.stringify({ type }),
    });
    const data = await res.json();
    if (res.ok) {
      notif.value.testMsg = `✅ ${data.message}`;
      notif.value.testOk  = true;
    } else {
      notif.value.testMsg = `❌ ${data.error || 'Erro ao enviar'}`;
      notif.value.testOk  = false;
    }
  } catch (e) {
    notif.value.testMsg = `❌ Erro: ${e.message}`;
    notif.value.testOk  = false;
  }
  notif.value.sending = false;
  setTimeout(() => { notif.value.testMsg = ''; }, 8000);
}

// ── WEATHER CONFIG ──
const weatherConfig = ref({
  weatherEnabled: false,
  weatherBudgetPercent: 30,
  weatherCities: 'nyc,chicago,los_angeles,miami',
  weatherapiKey: '',
  weatherMinEdge: 5,
  weatherMaxBets: 3,
});
const weatherSaveMsg = ref('');
const weatherSaveOk = ref(false);
const weatherSaving = ref(false);
const citySearch = ref('');
const allCities = ref([]);
const selectedCities = ref([]);
const openContinents = ref({});

const continentEmoji = {
  'America do Norte': '🇺🇸',
  'America do Sul': '🌎',
  'Europa': '🇪🇺',
  'Asia': '🌏',
  'Oceania / Africa': '🌍',
};

const COUNTRY_TO_CONTINENT = {
  US: 'America do Norte', CA: 'America do Norte', MX: 'America do Norte',
  BR: 'America do Sul', AR: 'America do Sul', CL: 'America do Sul', CO: 'America do Sul', PE: 'America do Sul',
  UK: 'Europa', FR: 'Europa', DE: 'Europa', RU: 'Europa', ES: 'Europa', IT: 'Europa',
  KR: 'Asia', JP: 'Asia', CN: 'Asia', IN: 'Asia', AE: 'Asia', SG: 'Asia', TH: 'Asia',
  AU: 'Oceania / Africa', EG: 'Oceania / Africa', ZA: 'Oceania / Africa',
};

const cityGroups = computed(() => {
  const q = citySearch.value.toLowerCase();
  const filtered = q
    ? allCities.value.filter(c => c.name.toLowerCase().includes(q) || c.country.toLowerCase().includes(q) || c.key.includes(q))
    : allCities.value;
  const groups = {};
  for (const c of filtered) {
    const cont = COUNTRY_TO_CONTINENT[c.country] || 'Outros';
    if (!groups[cont]) groups[cont] = [];
    groups[cont].push(c);
  }
  // Ordenar continentes
  const order = ['America do Norte', 'America do Sul', 'Europa', 'Asia', 'Oceania / Africa', 'Outros'];
  const sorted = {};
  for (const k of order) {
    if (groups[k]) sorted[k] = groups[k];
  }
  return sorted;
});

function toggleContinent(continent) {
  openContinents.value[continent] = !openContinents.value[continent];
}

async function loadCitiesList() {
  try {
    const base = getApiV1BaseUrl();
    const headers = authStore.token ? { Authorization: `Bearer ${authStore.token}` } : {};
    const res = await fetch(`${base}/weather/cities`, { headers });
    if (res.ok) {
      const data = await res.json();
      allCities.value = data.cities || [];
      // Abrir todos os continentes por padrão
      for (const c of Object.values(COUNTRY_TO_CONTINENT)) {
        openContinents.value[c] = true;
      }
    }
  } catch (_) {}
}

async function loadWeatherConfig() {
  try {
    const base = getApiV1BaseUrl();
    const headers = authStore.token ? { Authorization: `Bearer ${authStore.token}` } : {};
    const res = await fetch(`${base}/weather/config`, { headers });
    if (res.ok) {
      const data = await res.json();
      weatherConfig.value = { ...weatherConfig.value, ...data };
      // Sincronizar selectedCities com a config
      if (data.weatherCities) {
        selectedCities.value = data.weatherCities.split(',').filter(c => c.trim());
      }
    }
  } catch (_) {}
}

async function saveWeatherConfig() {
  weatherSaving.value = true;
  weatherSaveMsg.value = '';
  try {
    weatherConfig.value.weatherCities = selectedCities.value.join(',');
    const base = getApiV1BaseUrl();
    const headers = { 'Content-Type': 'application/json', ...(authStore.token ? { Authorization: `Bearer ${authStore.token}` } : {}) };
    const res = await fetch(`${base}/weather/config`, {
      method: 'PUT', headers,
      body: JSON.stringify(weatherConfig.value),
    });
    const data = await res.json();
    if (res.ok && data.success) {
      weatherSaveMsg.value = 'Configuracoes salvas com sucesso!';
      weatherSaveOk.value = true;
    } else {
      weatherSaveMsg.value = 'Erro ao salvar: ' + (data.error || 'desconhecido');
      weatherSaveOk.value = false;
    }
  } catch (e) {
    weatherSaveMsg.value = 'Erro de conexao: ' + e.message;
    weatherSaveOk.value = false;
  } finally {
    weatherSaving.value = false;
    setTimeout(() => { weatherSaveMsg.value = ''; }, 5000);
  }
}

onMounted(() => {
  loadSettings();
  loadBotLogs();
  loadAdminUsers();
  loadWeatherConfig();
  loadCitiesList();
  loadMetasConfig();
  loadNotifications();
  loadAgents();
  refreshTimer = setInterval(refreshBotState, 3000);
});

onUnmounted(() => {
  if (refreshTimer) { clearInterval(refreshTimer); refreshTimer = null; }
});
</script>

<style scoped>
.input {
  width: 100%;
  background: rgb(31 41 55);
  border: 1px solid rgb(55 65 81);
  border-radius: 0.75rem;
  padding: 0.5rem 0.875rem;
  font-size: 0.875rem;
  color: white;
}
.input:focus { outline: none; border-color: rgba(59, 130, 246, 0.5); }

.field-label {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.7rem;
  font-weight: 500;
  color: rgb(156 163 175);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.375rem;
}
.info-btn { color: rgb(107 114 128); transition: color 0.15s; font-style: normal; }
.info-btn:hover { color: rgb(96 165 250); }

.section-card { background: rgb(17 24 39); border-radius: 1rem; border: 1px solid rgb(31 41 55); overflow: hidden; }
.section-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: white;
  background: rgba(31 41 55 / 0.3);
  border-bottom: 1px solid rgb(31 41 55);
}

.toggle-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.875rem 1rem;
  background: rgba(31, 41, 55, 0.5);
  border: 1px solid rgba(55, 65, 81, 0.5);
  border-radius: 0.75rem;
  color: white;
  font-size: 0.875rem;
  transition: border-color 0.15s;
}
.toggle-card:hover { border-color: rgba(75, 85, 99, 0.8); }

.action-green, .action-red, .action-yellow {
  padding: 0.4rem 0.875rem;
  border-radius: 0.625rem;
  font-size: 0.75rem;
  font-weight: 600;
  transition: all 0.2s;
}
.action-green  { background: rgba(34, 197, 94, 0.15); color: rgb(74, 222, 128); border: 1px solid rgba(34, 197, 94, 0.2); }
.action-red    { background: rgba(239, 68, 68, 0.15); color: rgb(248, 113, 113); border: 1px solid rgba(239, 68, 68, 0.2); }
.action-yellow { background: rgba(234, 179, 8, 0.15); color: rgb(250, 204, 21); border: 1px solid rgba(234, 179, 8, 0.2); }
.action-green:disabled, .action-red:disabled { opacity: 0.3; cursor: not-allowed; }

.worker-console {
  background: radial-gradient(circle at top right, rgba(37, 99, 235, 0.16), transparent 32%),
              linear-gradient(180deg, rgba(10, 14, 24, 0.98), rgba(4, 8, 15, 0.98));
  font-family: Consolas, "Courier New", monospace;
}
.console-line { padding: 0.4rem 0.75rem; border-bottom: 1px solid rgba(30, 41, 59, 0.65); white-space: pre-wrap; word-break: break-word; font-size: 0.73rem; line-height: 1.45; }
.console-line:last-child { border-bottom: none; }
.console-info    { color: rgb(191, 219, 254); }
.console-gemini  { color: rgb(125, 211, 252); background: rgba(8, 47, 73, 0.22); }
.console-hero    { color: rgb(103, 232, 249); background: rgba(8, 51, 68, 0.3); }
.console-win     { color: rgb(94, 234, 212); background: rgba(15, 118, 110, 0.20); }
.console-loss    { color: rgb(253, 186, 116); background: rgba(154, 52, 18, 0.22); }
.console-risk    { color: rgb(253, 224, 71); background: rgba(113, 63, 18, 0.24); }
.console-state   { color: rgb(191, 219, 254); background: rgba(30, 41, 59, 0.18); }
.console-warning { color: rgb(253, 224, 71); background: rgba(113, 63, 18, 0.2); }
.console-error   { color: rgb(252, 165, 165); background: rgba(127, 29, 29, 0.22); }
.console-buy     { color: rgb(110, 231, 183); background: rgba(6, 78, 59, 0.18); }
.console-sell    { color: rgb(251, 191, 36); background: rgba(120, 53, 15, 0.18); }
.console-redeem  { color: rgb(196, 181, 253); background: rgba(76, 29, 149, 0.18); }
.console-empty   { color: rgb(148, 163, 184); }
</style>
